import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReviewPhotoLightbox from './ReviewPhotoLightbox';
import { fetchSpotlightReviews } from '../utils/socialProofClient';
import { cldThumb } from '../utils/cloudinary';
import '../styles/CustomerSpotlight.css';

/**
 * <CustomerSpotlight limit? />
 *
 * Homepage section that renders a visual grid of customer review photos with
 * rating overlay. Tap any photo → full lightbox with review details.
 *
 * Pulls from /api/reviews/spotlight which:
 *   1. Returns manually spotlighted reviews first (admin curated)
 *   2. Falls back to top-rated reviews with photos
 *
 * If there are <3 reviews with photos available, the section doesn't render
 * (avoids showing an anemic gallery).
 */
const CustomerSpotlight = ({ limit = 8, title = 'Our Customers' }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxReview, setLightboxReview] = useState(null);
  const [lightboxStart, setLightboxStart] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { reviews: r } = await fetchSpotlightReviews(limit);
        if (alive) setReviews(r || []);
      } catch (_) {
        // silent fail
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [limit]);

  if (loading) return null;   // don't render skeleton — just silently wait
  if (reviews.length < 3) return null;  // need meaningful volume

  // Flatten all photos across reviews (each photo remembers its source review)
  const photos = [];
  reviews.forEach((r) => {
    (r.images || []).forEach((img, i) => {
      photos.push({ ...img, review: r, indexInReview: i });
    });
  });

  const openLightbox = (photo) => {
    setLightboxReview(photo.review);
    setLightboxStart(photo.indexInReview);
  };

  return (
    <section className="cs-section">
      <div className="cs-container">
        <div className="cs-header">
          <h2 className="cs-title">{title}</h2>
          <p className="cs-subtitle">
            Real photos from real customers — tap any image to read their review
          </p>
        </div>

        <div className="cs-grid">
          {photos.slice(0, limit).map((p, i) => (
            <button
              key={`${p.review._id}-${p.indexInReview}`}
              type="button"
              className="cs-photo-card"
              onClick={() => openLightbox(p)}
              style={{ animationDelay: `${i * 0.04}s` }}
            >
              <img src={cldThumb(p.url)} alt={`Customer photo ${i + 1}`} loading="lazy" />
              <div className="cs-photo-overlay">
                <div className="cs-overlay-stars">
                  {'★'.repeat(p.review.rating || 5)}
                </div>
                <div className="cs-overlay-name">{p.review.authorName}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="cs-footer">
          <Link to="/shop" className="cs-shop-link">
            Shop the looks →
          </Link>
        </div>
      </div>

      {lightboxReview && (
        <ReviewPhotoLightbox
          images={lightboxReview.images}
          startIndex={lightboxStart}
          onClose={() => setLightboxReview(null)}
          reviewMeta={{
            rating: lightboxReview.rating,
            authorName: lightboxReview.authorName,
            comment: lightboxReview.comment,
          }}
        />
      )}
    </section>
  );
};

export default CustomerSpotlight;
